
import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface LanguageToggleProps {
  isScrolled?: boolean;
  isMobile?: boolean;
}

export default function LanguageToggle({ isScrolled = false, isMobile = false }: LanguageToggleProps) {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentLang = i18n.language === 'es' ? 'es' : 'en';

  const languages = [
    { code: 'en', label: 'English', flag: '🇺🇸' },
    { code: 'es', label: 'Español', flag: '🇲🇽' }
  ];

  const changeLanguage = (langCode: string) => {
    i18n.changeLanguage(langCode);
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (isMobile) {
    return (
      <div className="flex items-center gap-2">
        {languages.map((lang) => (
          <button
            key={lang.code}
            onClick={() => changeLanguage(lang.code)}
            className={`px-4 py-2 rounded-full font-bold text-sm uppercase tracking-wide transition-all cursor-pointer ${
              currentLang === lang.code
                ? 'bg-amber-700 text-white'
                : 'bg-gray-800 text-amber-100 hover:bg-gray-700'
            }`}
          >
            {lang.flag} {lang.code.toUpperCase()}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full font-bold text-sm uppercase tracking-wide transition-all cursor-pointer ${
          isScrolled
            ? 'bg-amber-100 text-amber-800 hover:bg-amber-200'
            : 'bg-white/20 text-amber-100 hover:bg-white/30 backdrop-blur-sm'
        }`}
      >
        <span>{languages.find(l => l.code === currentLang)?.flag}</span>
        <span>{currentLang.toUpperCase()}</span>
        <i className={`ri-arrow-down-s-line text-base transition-transform ${isOpen ? 'rotate-180' : ''}`}></i>
      </button>

      {isOpen && (
        <div className="absolute top-full right-0 mt-2 bg-white rounded-lg shadow-xl border border-gray-100 overflow-hidden min-w-[140px] z-50">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => changeLanguage(lang.code)}
              className={`w-full flex items-center gap-2 px-4 py-2.5 text-left text-sm font-medium transition-colors cursor-pointer ${
                currentLang === lang.code
                  ? 'bg-amber-50 text-amber-800'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <span>{lang.flag}</span>
              <span>{lang.label}</span>
              {currentLang === lang.code && (
                <i className="ri-check-line text-amber-700 ml-auto"></i>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
